<script setup lang="ts">
import { useHead } from '@unhead/vue';
import Banner from '@/components/shared/Banner.vue';

useHead({
  title: 'Providers | Student Assurance Services',
  meta: [
    {
      name: 'description',
      content:
        'Information for healthcare providers about submitting claims and checking claim status.',
    },
  ],
});
</script>

<template>
  <Banner image="providers" alt="Healthcare providers">
    <h1>Healthcare<br />Provider Information</h1>
  </Banner>
  <div class="container-sm flex flex-col gap-30 py-48 *:pr-[7%]">
    <h2 class="xl:text-39 md:text-29 text-24 text-teal font-bold">Claim Status</h2>
    <p>
      Student Assurance Services, Inc. partners with the U.S. Bank Payment Accelerator program
      powered by InstaMed to provide payments and claim information electronically. To learn more or
      register for this service, please select the link:
      <a href="https://register.instamed.com/eraeft-usbank">Payment Accelerator Program.</a>
    </p>
    <p>
      We do not guarantee or verify benefits, payments, or student eligibility. Benefits eligibility
      are determined when the claim is submitted to us. You may review or obtain a copy of the plan
      benefits from the student or on our website:
    </p>
    <ul class="-mt-30">
      <li>on the Home page, select "Find My School";</li>
      <li>then select the state where the school is located;</li>
      <li>then search and select the school name from the drop-down box;</li>
      <li>
        the school webpage provides the plan summary, claim form, and policy identification form.
      </li>
    </ul>
    <p>
      Students and school administrators (if the accident occurs at school) must complete a company
      claim form and send the claim form to us prior to or along with itemized bills. Only one claim
      form needs to be submitted for each accident.
    </p>
    <p>
      Our accident plans provide coverage on a secondary/excess basis. If the student has other
      insurance coverage, the student must send the bills to the primary insurance plan first. We
      will consider benefits after receiving the primary insurance plan's explanation of benefits
      (EOB). We cannot consider benefits without this information.
    </p>
    <p>
      All bills must include: name of doctor, doctor's complete mailing address; telephone number;
      federal tax ID number and NPI number; dates of service; procedure codes for each treatment or
      service provided; diagnosis codes; and itemized charges. Balance due statements cannot be
      processed. We accept CMS1500, UB04 and standard dental insurance bills.
    </p>
    <p>
      Please allow thirty (30) days after submitting your bills before calling us to check on claim
      status.
    </p>
    <p>
      There is a timely filing deadline of one (1) year and ninety (90) days from the date of
      service to submit a claim. Note timely filing requirements can vary based on the policy and
      state.
    </p>
    <p>
      We do not issue student ID cards for accident only plan coverage. If you need an ID number the
      date of birth may be used.
    </p>
    <h2 class="xl:text-39 md:text-29 text-24 text-teal leading-none font-bold">
      For Comprehensive Accident and<br />Sickness Insurance Plans
    </h2>
    <p>
      Student Assurance Services, Inc. partners with Wellfleet Student Insurance to process health
      claims. To view or verify plan benefits or to check claim status contact Wellfleet. A direct
      link to Wellfleet's website is provided below.
    </p>
    <div class="lg:text-24 text-20 text-teal font-bold">Wellfleet Student Insurance</div>
    <p>
      PO Box 15369, Springfield, MA 01115-5369<br />Phone: 1-877-657-5030; Claims Fax:
      (413)733-4612<br />
      <a href="https://www.wellfleetstudent.com" target="_blank">www.wellfleetstudent.com</a>
    </p>
    <p>
      A Wellfleet website link to the college or university is available on our Home webpage under
      "Find My School". Wellfleet contact information is also located on the student's ID card.
    </p>
  </div>
</template>
